import { useFeatureGate, useStatsigClient } from '@statsig/react-bindings'

import {
   CAPTCHA_APPLICATION_ACTION_GATE,
   CAPTCHA_APPLICATION_SEARCH_GATE,
   CAPTCHA_LOGIN_GATE,
   CAPTCHA_TDR_OPEN_SEARCH_GATE
} from '../constants/FeatureGatesConstants'
import { getFailSecureGateValue } from '../utils/captchaGateUtils'
import { getCaptchaGateName } from '../utils/captchaGateScopeUtils'
import { useCaptchaGateScope } from '../contexts/CaptchaGateScopeContext'

export interface CaptchaGates {
   isStatsigReady: boolean
   isLoginCaptchaEnabled: boolean
   isApplicationSearchCaptchaEnabled: boolean
   isTDROpenSearchCaptchaEnabled: boolean
   isApplicationActionCaptchaEnabled: boolean
}

// Fail-secure: captcha stays ON unless Statsig is a real (non-Noop),
// Ready client AND the gate evaluation reason is trusted. See
// captchaGateUtils.getFailSecureGateValue for the rule itself.
//
// The gate name read for each feature is suffixed with the app/portal scope
// (e.g. enable_captcha_login_officer), so each build toggles independently,
// mirroring the pre-migration per-.env-file env variables. Scope is fixed for
// the process lifetime, so useFeatureGate is still called exactly 4 times in a
// fixed order (rules of hooks).
const useCaptchaGates = (): CaptchaGates => {
   const { client } = useStatsigClient()
   const isStatsigReady =
      !('isNoop' in client) && client.loadingStatus === 'Ready'

   const scope = useCaptchaGateScope()

   const loginGate = useFeatureGate(
      getCaptchaGateName(CAPTCHA_LOGIN_GATE, scope)
   )
   const applicationSearchGate = useFeatureGate(
      getCaptchaGateName(CAPTCHA_APPLICATION_SEARCH_GATE, scope)
   )
   const tdrOpenSearchGate = useFeatureGate(
      getCaptchaGateName(CAPTCHA_TDR_OPEN_SEARCH_GATE, scope)
   )
   const applicationActionGate = useFeatureGate(
      getCaptchaGateName(CAPTCHA_APPLICATION_ACTION_GATE, scope)
   )

   return {
      isStatsigReady,
      isLoginCaptchaEnabled: getFailSecureGateValue({
         isStatsigReady,
         gate: loginGate
      }),
      isApplicationSearchCaptchaEnabled: getFailSecureGateValue({
         isStatsigReady,
         gate: applicationSearchGate
      }),
      isTDROpenSearchCaptchaEnabled: getFailSecureGateValue({
         isStatsigReady,
         gate: tdrOpenSearchGate
      }),
      isApplicationActionCaptchaEnabled: getFailSecureGateValue({
         isStatsigReady,
         gate: applicationActionGate
      })
   }
}

export default useCaptchaGates
